import React, { useState, useEffect, useCallback } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Send, 
  Mail, 
  Calendar, 
  Clock, 
  CheckCircle, 
  XCircle,
  BarChart3,
  Users,
  Search,
  Filter,
  Loader2
} from 'lucide-react';
import { useToastContext } from '../context/ToastContext';
import { useEvent } from '../context/EventContext';
import Layout from '../components/layout/Layout';
import EmailComposer from '../components/email/EmailComposer';
import Loading from '../components/common/Loading';
import ConfirmModal from '../components/common/ConfirmModal';
import { eventAPI, attendeeAPI } from '../api/endpoints';
import api from '../api/axios';

const EmailCampaigns = () => {
  const toast = useToastContext();
  const { selectedEvent, setSelectedEvent } = useEvent();
  const [campaigns, setCampaigns] = useState([]);
  const [attendees, setAttendees] = useState([]);
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('compose');
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showStats, setShowStats] = useState({});

  // Confirm modals
  const [confirmSend, setConfirmSend] = useState<{ open: boolean; campaignId: string | null }>({ open: false, campaignId: null });
  const [confirmDelete, setConfirmDelete] = useState<{ open: boolean; campaignId: string | null }>({ open: false, campaignId: null });

  // Define functions before using them in useEffect
  const loadEvents = useCallback(async () => {
    try {
      console.log('[Email Campaigns] Loading events...');
      const response = await eventAPI.getAll({});
      console.log('[Email Campaigns] Events response:', response);
      
      const eventsList = response.data?.events || response.data || [];
      setEvents(eventsList);
      
      if (eventsList.length > 0) {
        if (!selectedEvent) {
          setSelectedEvent(eventsList[0].id);
          console.log('[Email Campaigns] Selected first event:', eventsList[0].id);
        }
      } else {
        console.log('[Email Campaigns] No events available');
      }
    } catch (error) {
      console.error('[Email Campaigns] Failed to load events:', error);
      toast.error('Failed to load events');
    }
  }, [selectedEvent, setSelectedEvent]);

  const loadCampaigns = useCallback(async () => {
    if (!selectedEvent) return;

    setIsLoading(true);
    try {
      const response = await api.get(`events/${selectedEvent}/email-campaigns`);
      setCampaigns(response.data.data?.data || []);
      console.log('[Email Campaigns] Campaigns loaded:', response.data.data?.data?.length || 0);
    } catch (error) {
      console.error('[Email Campaigns] Failed to load campaigns:', error);
      toast.error('Failed to load email campaigns');
    } finally {
      setIsLoading(false);
    }
  }, [selectedEvent]);

  const loadAttendees = useCallback(async () => {
    if (!selectedEvent) return;

    try {
      console.log('[Email Campaigns] Loading attendees for event:', selectedEvent);
      const response = await attendeeAPI.getAll(selectedEvent, {});
      console.log('[Email Campaigns] Attendees response:', response);
      
      const attendeesList = response.data?.attendees || response.data || [];
      setAttendees(attendeesList);
      console.log('[Email Campaigns] Attendees loaded:', attendeesList.length);
    } catch (error) {
      console.error('[Email Campaigns] Failed to load attendees:', error);
    }
  }, [selectedEvent]);

  const handleDeleteCampaign = async (campaignId) => {
    setConfirmDelete({ open: true, campaignId });
  };

  const confirmDeleteCampaign = async () => {
    const campaignId = confirmDelete.campaignId;
    setConfirmDelete({ open: false, campaignId: null });
    if (!campaignId) return;

    try {
      await api.delete(`events/${selectedEvent}/email-campaigns/${campaignId}`);
      toast.success('Campaign deleted successfully');
      loadCampaigns();
    } catch (error) {
      console.error('Failed to delete campaign:', error);
      toast.error('Failed to delete campaign');
    }
  };

  const handleSendCampaign = async (campaignId) => {
    setConfirmSend({ open: true, campaignId });
  };

  const [activeProgressMap, setActiveProgressMap] = useState<Record<string, number>>({});
  const [progressDataMap, setProgressDataMap] = useState<Record<string, any>>({});

  // Poll progress for active campaigns
  useEffect(() => {
    const activeIds = Object.keys(activeProgressMap);
    if (activeIds.length === 0) return;

    const interval = setInterval(async () => {
      for (const campaignId of activeIds) {
        const progressId = activeProgressMap[campaignId];
        try {
          const res = await api.get(`campaigns/progress/${progressId}`);
          const data = res.data.data;
          
          setProgressDataMap(prev => ({
            ...prev,
            [campaignId]: data
          }));

          if (data.status === 'completed' || data.status === 'failed') {
            setActiveProgressMap(prev => {
              const next = { ...prev };
              delete next[campaignId];
              return next;
            });
            
            if (data.status === 'completed') {
              toast.success(`Email campaign processing complete!`);
            } else {
              toast.error(`Email campaign failed to process completely.`);
            }
            loadCampaigns();
          }
        } catch (err) {
          console.error('Failed to fetch progress:', err);
        }
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [activeProgressMap, loadCampaigns]);

  const confirmSendCampaign = async () => {
    const campaignId = confirmSend.campaignId;
    setConfirmSend({ open: false, campaignId: null });
    if (!campaignId) return;

    try {
      const res = await api.post(`events/${selectedEvent}/email-campaigns/${campaignId}/send`);
      if (res.data.progress_id) {
        setActiveProgressMap(prev => ({
          ...prev,
          [campaignId]: res.data.progress_id
        }));
        toast.info('Campaign queued in background. Tracking progress...');
      } else {
        toast.success('Campaign is being sent');
      }
      loadCampaigns();
    } catch (error) {
      console.error('Failed to send campaign:', error);
      toast.error('Failed to send campaign');
    }
  };

  const loadCampaignStats = async (campaignId) => {
    try {
      const response = await api.get(`events/${selectedEvent}/email-campaigns/${campaignId}/stats`);
      setShowStats(prev => ({
        ...prev,
        [campaignId]: response.data.data
      }));
    } catch (error) {
      console.error('Failed to load campaign stats:', error);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'draft':
        return <Edit className="w-4 h-4 text-gray-500" />;
      case 'scheduled':
        return <Clock className="w-4 h-4 text-blue-500" />;
      case 'sending':
        return <Send className="w-4 h-4 text-yellow-500" />;
      case 'sent':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Mail className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'scheduled':
        return 'bg-blue-100 text-blue-800';
      case 'sending':
        return 'bg-yellow-100 text-yellow-800';
      case 'sent':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // useEffect hooks after function definitions
  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  useEffect(() => {
    if (selectedEvent) {
      loadCampaigns();
      loadAttendees();
    }
  }, [selectedEvent, loadCampaigns, loadAttendees]);

  const filteredCampaigns = campaigns.filter(campaign => {
    const matchesSearch = campaign.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         campaign.subject.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || campaign.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (!selectedEvent) {
    return (
      <div className="p-6">
        <div className="text-center">
          <Mail className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No event selected</h3>
          <p className="mt-1 text-sm text-gray-500">
            Please select an event to view and manage email campaigns.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Email Campaigns</h1>
            <p className="text-gray-600">Create and manage email campaigns for your events</p>
          </div>
          {activeTab === 'logs' && (
            <button
              onClick={() => {
                setEditingCampaign(null);
                setActiveTab('compose');
              }}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-teal-600 to-emerald-600 text-white text-sm font-semibold rounded-xl hover:from-teal-700 hover:to-emerald-700 active:scale-[0.98] shadow-md hover:shadow-lg transition-all duration-150"
            >
              <Plus className="w-4 h-4" />
              New Campaign
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex gap-8 overflow-x-auto whitespace-nowrap">
            <button
              onClick={() => setActiveTab('compose')}
              className={`
                whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors
                ${activeTab === 'compose'
                  ? 'border-teal-500 text-teal-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              Compose campaign
            </button>
            <button
              onClick={() => setActiveTab('logs')}
              className={`
                whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors
                ${activeTab === 'logs'
                  ? 'border-teal-500 text-teal-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              Campaign logs
            </button>
          </nav>
        </div>

        {/* Event Selector */}
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Event
          </label>
          <select
            value={selectedEvent}
            onChange={(e) => setSelectedEvent(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
          >
            <option value="">Select an event</option>
            {events.map(event => (
              <option key={event.id} value={event.id}>
                {event.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {activeTab === 'logs' && (
        <>
          {/* Filters */}
          <div className="mb-6 flex items-center gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search campaigns..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-500" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="all">All Status</option>
                <option value="draft">Draft</option>
                <option value="scheduled">Scheduled</option>
                <option value="sending">Sending</option>
                <option value="sent">Sent</option>
                <option value="failed">Failed</option>
              </select>
            </div>
          </div>

          {/* Campaigns List */}
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loading />
            </div>
          ) : filteredCampaigns.length === 0 ? (
            <div className="text-center py-8">
              <Mail className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No campaigns found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {campaigns.length === 0 
                  ? "Get started by creating your first email campaign."
                  : "Try adjusting your search or filter criteria."
                }
              </p>
              {campaigns.length === 0 && (
                <button
                  onClick={() => setActiveTab('compose')}
                  className="mt-4 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
                >
                  Create Campaign
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredCampaigns.map(campaign => (
                <div key={campaign.id} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        {getStatusIcon(campaign.status)}
                    <h3 className="text-lg font-semibold text-gray-900">{campaign.name}</h3>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(campaign.status)}`}>
                      {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
                    </span>
                  </div>
                  
                  <p className="text-gray-600 mb-3">{campaign.subject}</p>
                  
                  <div className="flex items-center gap-6 text-sm text-gray-500">
                    <div className="flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      <span>{campaign.total_recipients} recipients</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      <span>{new Date(campaign.created_at).toLocaleDateString()}</span>
                    </div>
                    {campaign.sent_at && (
                      <div className="flex items-center gap-1">
                        <Send className="w-4 h-4" />
                        <span>Sent {new Date(campaign.sent_at).toLocaleDateString()}</span>
                      </div>
                    )}
                  </div>

                  {/* Progress Bar UI */}
                  {activeProgressMap[campaign.id] && progressDataMap[campaign.id] && (
                    <div className="mt-4 border border-teal-200 bg-teal-50 rounded-lg p-4 space-y-3 shadow-sm">
                      <div className="flex justify-between text-sm font-medium text-teal-800">
                        <span className="flex items-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Processing Campaign in Background...
                        </span>
                        <span>{Math.round((progressDataMap[campaign.id].processed_count / progressDataMap[campaign.id].total_recipients) * 100) || 0}%</span>
                      </div>
                      <div className="w-full bg-teal-200 rounded-full h-2.5 overflow-hidden">
                        <div 
                          className="bg-teal-600 h-2.5 rounded-full transition-all duration-500 ease-out" 
                          style={{ width: `${Math.round((progressDataMap[campaign.id].processed_count / progressDataMap[campaign.id].total_recipients) * 100) || 0}%` }}
                        ></div>
                      </div>
                      <div className="flex justify-between text-xs text-teal-700 font-medium">
                        <span>Processed: {progressDataMap[campaign.id].processed_count} / {progressDataMap[campaign.id].total_recipients}</span>
                        <span>Success: {progressDataMap[campaign.id].success_count} | Failed: <span className={progressDataMap[campaign.id].failure_count > 0 ? "text-red-600 font-bold" : ""}>{progressDataMap[campaign.id].failure_count}</span></span>
                      </div>
                    </div>
                  )}

                  {/* Stats */}
                  {campaign.status === 'sent' && !activeProgressMap[campaign.id] && (
                    <div className="mt-4 grid grid-cols-3 gap-4">
                      <div className="text-center p-3 bg-green-50 rounded-lg">
                        <div className="text-lg font-semibold text-green-600">{campaign.delivery_rate}%</div>
                        <div className="text-xs text-green-700">Delivered</div>
                      </div>
                      <div className="text-center p-3 bg-blue-50 rounded-lg">
                        <div className="text-lg font-semibold text-blue-600">{campaign.open_rate}%</div>
                        <div className="text-xs text-blue-700">Opened</div>
                      </div>
                      <div className="text-center p-3 bg-purple-50 rounded-lg">
                        <div className="text-lg font-semibold text-purple-600">{campaign.click_rate}%</div>
                        <div className="text-xs text-purple-700">Clicked</div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 ml-4 flex-shrink-0">
                  {campaign.status === 'sent' && (
                    <button
                      onClick={() => {
                        if (showStats[campaign.id]) {
                          setShowStats(prev => ({ ...prev, [campaign.id]: null }));
                        } else {
                          loadCampaignStats(campaign.id);
                        }
                      }}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                      title="View Stats"
                    >
                      <BarChart3 className="w-3.5 h-3.5" />
                      <span className="hidden sm:inline">Stats</span>
                    </button>
                  )}
                  
                  {campaign.status === 'draft' && (
                    <>
                      <button
                        onClick={() => {
                          setEditingCampaign(campaign);
                          setActiveTab('compose');
                        }}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <Edit className="w-3.5 h-3.5" />
                        <span className="hidden sm:inline">Edit</span>
                      </button>
                      <button
                        onClick={() => handleSendCampaign(campaign.id)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-teal-700 hover:text-white hover:bg-teal-600 rounded-lg transition-colors border border-teal-200 hover:border-teal-600"
                        title="Send Campaign"
                      >
                        <Send className="w-3.5 h-3.5" />
                        <span className="hidden sm:inline">Send</span>
                      </button>
                    </>
                  )}
                  
                  {campaign.status !== 'sending' && (
                    <button
                      onClick={() => handleDeleteCampaign(campaign.id)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-500 hover:text-white hover:bg-red-500 rounded-lg transition-colors border border-transparent hover:border-red-500"
                      title="Delete"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      <span className="hidden sm:inline">Delete</span>
                    </button>
                  )}
                </div>
              </div>

              {/* Detailed Stats */}
              {showStats[campaign.id] && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Detailed Statistics</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <div className="text-gray-500">Total Sent</div>
                      <div className="font-semibold">{showStats[campaign.id].sent_count}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">Failed</div>
                      <div className="font-semibold text-red-600">{showStats[campaign.id].failed_count}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">Opened</div>
                      <div className="font-semibold text-blue-600">{showStats[campaign.id].opened_count}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">Clicked</div>
                      <div className="font-semibold text-purple-600">{showStats[campaign.id].clicked_count}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Email Composer Tab */}
      {activeTab === 'compose' && (
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
          <EmailComposer
            eventId={selectedEvent}
            attendees={attendees}
            initialData={editingCampaign}
            onSendComplete={(progressId, campaignId) => {
              if (progressId && campaignId) {
                setActiveProgressMap(prev => ({ ...prev, [campaignId]: progressId }));
                toast.info('Campaign queued in background. Tracking progress...');
              }
              loadCampaigns();
              setEditingCampaign(null);
              setActiveTab('logs');
            }}
          />
        </div>
      )}
      </div>

    {/* Send Campaign Confirmation Modal */}
    <ConfirmModal
      isOpen={confirmSend.open}
      onClose={() => setConfirmSend({ open: false, campaignId: null })}
      onConfirm={confirmSendCampaign}
      title="Send Email Campaign"
      message="Are you sure you want to send this campaign? It will be dispatched to all selected recipients immediately and cannot be undone."
      confirmText="Yes, Send Campaign"
      cancelText="Go Back"
      variant="warning"
    />

    {/* Delete Campaign Confirmation Modal */}
    <ConfirmModal
      isOpen={confirmDelete.open}
      onClose={() => setConfirmDelete({ open: false, campaignId: null })}
      onConfirm={confirmDeleteCampaign}
      title="Delete Campaign"
      message="Are you sure you want to delete this campaign? This action is permanent and cannot be recovered."
      confirmText="Yes, Delete"
      cancelText="Cancel"
      variant="danger"
    />
    </>
  );
};

export default EmailCampaigns;
